import { Question, QuestionExpressionModel } from '@psyomics/surveyjs-core';
import { z } from 'zod';

import { convertMarkdown } from '../../../../utils/convert-markdown';
import { Survey } from '../../survey-types';

enum ExpressionTypes {
  MEASURE = 'MEASURE',
  SUBSCALE = 'SUBSCALE',
}
const baseSchema = z
  .object({
    measureName: z.string().optional(),
    maxValue: z.number().optional(),
    subscale: z.boolean(),
  })
  .catchall(z.any());

const thresholdsSchema = z
  .object({
    text: z.string(),
    value: z.string(),
  })
  .catchall(z.any());

const measureSchema = baseSchema
  .extend({
    conditionName: z.string().optional(),
    thresholds: z.array(thresholdsSchema),
    subscale: z.literal(false),
  })
  .catchall(z.any());

type Threshold = z.infer<typeof thresholdsSchema>;

const subscaleSchema = baseSchema.extend({
  subscale: z.literal(true),
  maxValue: z.coerce.number(),
  thresholds: z.array(thresholdsSchema).optional(),
});

interface IQuestionExpressionModelForMeasure extends z.infer<typeof measureSchema>, QuestionExpressionModel {}

interface IQuestionExpressionModelForSubscale extends z.infer<typeof subscaleSchema>, QuestionExpressionModel {}

type QuestionExpression = IQuestionExpressionModelForMeasure | IQuestionExpressionModelForSubscale;

const parseExpression = (
  question: Question
): IQuestionExpressionModelForMeasure | IQuestionExpressionModelForSubscale => {
  const expressionQuestion = z.instanceof(QuestionExpressionModel).safeParse(question);

  if (expressionQuestion.error) {
    throw new Error(expressionQuestion.error.message);
  }

  const subscaleResult = subscaleSchema.safeParse(question);

  if (subscaleResult.success) {
    return Object.assign(expressionQuestion.data, subscaleResult.data);
  }

  if (question.subscale) {
    throw new Error(`Question ${question.name} does not match subscale schema: ` + subscaleResult.error?.message);
  }

  const measureResult = measureSchema.safeParse(question);

  if (measureResult.success) {
    return Object.assign(expressionQuestion.data, measureResult.data);
  }

  throw new Error(`Question ${question.name} does not match measure schema: ` + measureResult.error?.message);
};

const isMeasureExpression = (expression: QuestionExpression): expression is IQuestionExpressionModelForMeasure => {
  return expression.subscale === false;
};

const isSubscaleExpression = (expression: QuestionExpression): expression is IQuestionExpressionModelForSubscale => {
  return expression.subscale === true;
};

const getExpressionQuestions = (survey: Survey, type?: ExpressionTypes) => {
  const questions = survey.model.getAllQuestions();
  return questions
    .filter((question) => question.getType() === 'expression')
    .filter((q) => {
      if (type === undefined) return true;
      return type === ExpressionTypes.SUBSCALE ? q.subscale === true : q.subscale === false;
    })
    .map(parseExpression);
};

const getExpressionFromSurvey = (survey: Survey, expressionName?: string) => {
  if (!expressionName) return;

  const x = survey.model.getQuestionByName(expressionName);
  return x ? parseExpression(x) : undefined;
};

const getMeasureValue = (value: unknown) => {
  const { data } = z.coerce.number().safeParse(value);

  return data ?? 0;
};

const getThresholdByScore = (
  score: number,
  expression: IQuestionExpressionModelForMeasure | IQuestionExpressionModelForSubscale
): { min: Threshold | undefined; max: Threshold | undefined } => {
  const thresholds = expression.thresholds || [];
  const thresholdIndex = thresholds.findIndex((threshold, index, array) => {
    const nextValue = parseInt(array[index + 1]?.value);
    const value = Number.parseInt(threshold.value);

    return score >= value && (!nextValue || score < nextValue);
  });

  return {
    min: thresholdIndex !== -1 ? thresholds[thresholdIndex] : undefined,
    max: thresholdIndex !== -1 && thresholds[thresholdIndex + 1] ? thresholds[thresholdIndex + 1] : undefined,
  };
};

const getCalculationId = (expression: QuestionExpressionModel, respondentType: string) => {
  return `${expression.conditionName}-${expression.measureName}-${respondentType}`;
};

const getExpressionRenderedText = (survey: Survey, expression: QuestionExpressionModel) => {
  const output = survey.model.getProcessedText(expression.description);
  return convertMarkdown(output);
};

export {
  ExpressionTypes,
  getCalculationId,
  getExpressionFromSurvey,
  getExpressionQuestions,
  getExpressionRenderedText,
  getMeasureValue,
  getThresholdByScore,
  type IQuestionExpressionModelForMeasure,
  type IQuestionExpressionModelForSubscale,
  isMeasureExpression,
  isSubscaleExpression,
  parseExpression,
  type QuestionExpression,
  type Threshold,
};
